import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, RotateCw, Gavel, Printer, ChevronDown, Upload } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { getEventsByRegion, EventByRegion, getJudgeSetsByRegion, JudgeSet, saveHjSetForEvent, SaveHjSetMapping, getEventsWithJudgeSetsByRegion, EventWithJudgeSet, getRaceDirectors, createUsersFromRaceOfficial, createHjFromCsv, CreateHjFromCsvResult, getRaceOfficialCreds, RaceOfficialCredential, saveJudgesetEventActiveStatus, EventActiveStatus } from '../../lib/api';
import { parseHjCsvFile } from '../../lib/parseHjCsv';

interface HeadJudgesTimingTileProps {
  // No props needed - self-contained
}

export const HeadJudgesTimingTile: React.FC<HeadJudgesTimingTileProps> = () => {
  const { user } = useAuth();
  const [selectedRegion, setSelectedRegion] = useState<'EU' | 'APAC' | 'AFRICA' | 'AMERICAS' | null>(null);
  const [regionEvents, setRegionEvents] = useState<EventByRegion[]>([]);
  const [isLoadingRegionEvents, setIsLoadingRegionEvents] = useState(false);
  const [eventsWithJudgeSets, setEventsWithJudgeSets] = useState<EventWithJudgeSet[]>([]);
  const [isLoadingEventsWithJudgeSets, setIsLoadingEventsWithJudgeSets] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [judgeSets, setJudgeSets] = useState<JudgeSet[]>([]);
  const [isLoadingJudgeSets, setIsLoadingJudgeSets] = useState(false);
  const [eventJudgeSetAssignments, setEventJudgeSetAssignments] = useState<Record<number, number | null>>({});
  const [availableRaceDirectors, setAvailableRaceDirectors] = useState<string[]>([]);
  const [isLoadingRaceDirectors, setIsLoadingRaceDirectors] = useState(false);
  const [eventRaceDirectorsAssignments, setEventRaceDirectorsAssignments] = useState<Record<number, string[]>>({});
  const [openTimingDropdown, setOpenTimingDropdown] = useState<number | null>(null);
  const [isCreatingUsers, setIsCreatingUsers] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvParseError, setCsvParseError] = useState('');
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [csvResults, setCsvResults] = useState<CreateHjFromCsvResult[] | null>(null);
  const [csvSummary, setCsvSummary] = useState<{ created: number; failed: number; skipped: number } | null>(null);
  const [showCredsModal, setShowCredsModal] = useState(false);
  const [selectedJudgesetnum, setSelectedJudgesetnum] = useState<number | null>(null);
  const [credentials, setCredentials] = useState<RaceOfficialCredential[]>([]);
  const [isLoadingCreds, setIsLoadingCreds] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRData, setSelectedQRData] = useState<{email: string, password: string} | null>(null);
  const [activeEvents, setActiveEvents] = useState<Record<number, number | null>>({});
  const [isSavingActiveStatus, setIsSavingActiveStatus] = useState(false);
  const [hasCheckedActiveStatus, setHasCheckedActiveStatus] = useState(false);

  // Format date to DD-mmm-YYYY (e.g., "15-Jan-2024")
  const formatDateForDisplay = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Fetch events by region when region is selected
  useEffect(() => {
    const fetchRegionEvents = async () => {
      if (!selectedRegion) {
        setRegionEvents([]);
        setEventsWithJudgeSets([]);
        return;
      }

      setIsLoadingRegionEvents(true);
      setIsLoadingEventsWithJudgeSets(true);
      try {
        const eventsWithJudgeSetsResult = await getEventsWithJudgeSetsByRegion(selectedRegion);
        if (eventsWithJudgeSetsResult.success) {
          setEventsWithJudgeSets(eventsWithJudgeSetsResult.events);
          const initialActiveEvents: Record<number, number | null> = {};
          eventsWithJudgeSetsResult.events.forEach(event => {
            if (event.judgesetnum !== null) {
              if (event.islive) {
                initialActiveEvents[event.judgesetnum] = event.eventid;
              } else {
                if (!(event.judgesetnum in initialActiveEvents)) {
                  initialActiveEvents[event.judgesetnum] = null;
                }
              }
            }
          });
          setActiveEvents(initialActiveEvents);
        } else {
          console.error('Failed to fetch events with judge sets:', eventsWithJudgeSetsResult.error);
          setEventsWithJudgeSets([]);
        }

        const result = await getEventsByRegion(selectedRegion);
        if (result.success) {
          setRegionEvents(result.events);
          const initialAssignments: Record<number, number | null> = {};
          result.events.forEach(event => {
            const eventWithJudgeSet = eventsWithJudgeSetsResult.events?.find(e => e.eventid === event.eventid);
            initialAssignments[event.eventid] = eventWithJudgeSet?.judgesetnum || null;
          });
          setEventJudgeSetAssignments(initialAssignments);
        } else {
          console.error('Failed to fetch events by region:', result.error);
          setRegionEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events by region:', error);
        setRegionEvents([]);
        setEventsWithJudgeSets([]);
      } finally {
        setIsLoadingRegionEvents(false);
        setIsLoadingEventsWithJudgeSets(false);
      }
    };

    fetchRegionEvents();
  }, [selectedRegion]);

  // Fetch judge sets by region when region is selected
  useEffect(() => {
    const fetchJudgeSets = async () => {
      if (!selectedRegion) {
        setJudgeSets([]);
        return;
      }

      setIsLoadingJudgeSets(true);
      try {
        const result = await getJudgeSetsByRegion(selectedRegion);
        if (result.success) {
          setJudgeSets(result.judgeSets);
        } else {
          console.error('Failed to fetch judge sets by region:', result.error);
          setJudgeSets([]);
        }
      } catch (error) {
        console.error('Error fetching judge sets by region:', error);
        setJudgeSets([]);
      } finally {
        setIsLoadingJudgeSets(false);
      }
    };

    fetchJudgeSets();
  }, [selectedRegion]);

  // Check if first occurrence of each unique judge set is ACTIVE for today's events
  useEffect(() => {
    if (isLoadingEventsWithJudgeSets || eventsWithJudgeSets.length === 0 || hasCheckedActiveStatus) {
      return;
    }

    const sortedEvents = [...eventsWithJudgeSets].sort((a, b) => {
      const dateA = a.eventdate ? new Date(a.eventdate).getTime() : 0;
      const dateB = b.eventdate ? new Date(b.eventdate).getTime() : 0;
      
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      const nameA = a.eventname || '';
      const nameB = b.eventname || '';
      return nameA.localeCompare(nameB);
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const judgesetFirstEvents = new Map<number, EventWithJudgeSet>();
    
    sortedEvents.forEach(event => {
      if (event.judgesetnum !== null) {
        if (!judgesetFirstEvents.has(event.judgesetnum)) {
          judgesetFirstEvents.set(event.judgesetnum, event);
        }
      }
    });

    let shouldShowAlert = false;
    
    judgesetFirstEvents.forEach((event) => {
      if (event.eventdate) {
        const eventDate = new Date(event.eventdate);
        eventDate.setHours(0, 0, 0, 0);
        
        if (eventDate.getTime() === today.getTime() && !event.islive) {
          shouldShowAlert = true;
        }
      }
    });

    if (shouldShowAlert) {
      alert("It is a new Race Day today. Please set today's Race to ACTIVE using the Set LIVE button");
      setHasCheckedActiveStatus(true);
    }
  }, [eventsWithJudgeSets, isLoadingEventsWithJudgeSets, hasCheckedActiveStatus]);

  // Reset check flag when region changes
  useEffect(() => {
    setHasCheckedActiveStatus(false);
  }, [selectedRegion]);

  // Fetch race directors when modal opens
  useEffect(() => {
    const fetchRaceDirectors = async () => {
      if (!showAssignModal) {
        return;
      }

      setIsLoadingRaceDirectors(true);
      try {
        const result = await getRaceDirectors();
        if (result.success) {
          setAvailableRaceDirectors(result.emails || []);
        } else {
          console.error('Failed to fetch race directors:', result.error);
          setAvailableRaceDirectors([]);
        }
      } catch (error) {
        console.error('Error fetching race directors:', error);
        setAvailableRaceDirectors([]);
      } finally {
        setIsLoadingRaceDirectors(false);
      }
    };

    fetchRaceDirectors();
  }, [showAssignModal]);

  // Initialize event race directors assignments when modal opens or region events change
  useEffect(() => {
    if (showAssignModal && regionEvents.length > 0) {
      const initialAssignments: Record<number, string[]> = {};
      regionEvents.forEach(event => {
        initialAssignments[event.eventid] = eventRaceDirectorsAssignments[event.eventid] || [];
      });
      setEventRaceDirectorsAssignments(initialAssignments);
    }
  }, [showAssignModal, regionEvents]);

  // Fetch credentials when credentials modal opens
  useEffect(() => {
    const fetchCredentials = async () => {
      if (!showCredsModal || selectedJudgesetnum === null) {
        return;
      }

      setIsLoadingCreds(true);
      try {
        const result = await getRaceOfficialCreds(selectedJudgesetnum);
        if (result.success) {
          setCredentials(result.credentials);
        } else {
          console.error('Failed to fetch credentials:', result.error);
          setCredentials([]);
          alert(`Failed to fetch credentials: ${result.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error fetching credentials:', error);
        setCredentials([]);
        alert('Failed to fetch credentials. Please try again.');
      } finally {
        setIsLoadingCreds(false);
      }
    };

    fetchCredentials();
  }, [showCredsModal, selectedJudgesetnum]);

  // Function to generate and download PDF
  const handlePrintToPDF = () => {
    if (credentials.length === 0 || selectedJudgesetnum === null) {
      return;
    }

    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Head Judge User Credentials', 14, 20);
    
    doc.setFontSize(12);
    doc.text(`Judge Set #${selectedJudgesetnum} - ${credentials.length} user(s)`, 14, 30);
    
    const tableData = credentials.map(cred => [cred.email, cred.password]);
    
    autoTable(doc, {
      head: [['Sign-in / e-mail', 'Password']],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [243, 244, 246],
        textColor: [55, 65, 81],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 80, font: 'courier' },
      },
    });
    
    const filename = `Head_Judge_Credentials_Judgeset_${selectedJudgesetnum}.pdf`;
    doc.save(filename);
  };

  // Function to generate and show QR code
  const handleGenerateQR = (email: string, password: string) => {
    setSelectedQRData({ email, password });
    setShowQRModal(true);
  };

  const handleProcessCsv = async () => {
    setCsvParseError('');
    setCsvResults(null);
    setCsvSummary(null);

    if (!csvFile) {
      setCsvParseError('Please select a CSV file');
      return;
    }

    setIsProcessingCsv(true);
    try {
      const parsed = await parseHjCsvFile(csvFile);
      if (!parsed.ok) {
        setCsvParseError(parsed.error);
        return;
      }

      const result = await createHjFromCsv(parsed.rows);
      if (!result.success) {
        setCsvParseError(result.error || 'Failed to create head judges from CSV');
        if (result.results) {
          setCsvResults(result.results);
          setCsvSummary(result.summary ?? null);
        }
        return;
      }

      setCsvResults(result.results ?? []);
      setCsvSummary(result.summary ?? null);
    } catch (error) {
      console.error('Error processing HJ CSV:', error);
      setCsvParseError(error instanceof Error ? error.message : 'Failed to process CSV');
    } finally {
      setIsProcessingCsv(false);
    }
  };

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Manage Head Judges across Races</h2>
            <div className="flex gap-3">
              {user?.email === 'reachavi@gmail.com' && (
                <>
                <Button 
                  onClick={async () => {
                    if (!confirm('This will create Supabase auth users for race officials. Continue?')) {
                      return;
                    }

                    setIsCreatingUsers(true);
                    try {
                      const result = await createUsersFromRaceOfficial();
                      if (result.success) {
                        alert(`Success! Created: ${result.created}, Skipped: ${result.skipped}, Failed: ${result.failed}`);
                      } else {
                        alert(`Error: ${result.error || 'Failed to create users'}`);
                      }
                    } catch (error) {
                      console.error('Error creating users:', error);
                      alert('Failed to create users. Please try again.');
                    } finally {
                      setIsCreatingUsers(false);
                    }
                  }}
                  disabled={isCreatingUsers}
                  className={isCreatingUsers 
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed" 
                    : "bg-green-600 hover:bg-green-700 text-white"
                  }
                >
                  {isCreatingUsers ? (
                    <>
                      <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Gavel className="w-4 h-4 mr-2" />
                      Create HJ
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setCsvFile(null);
                    setCsvParseError('');
                    setCsvResults(null);
                    setCsvSummary(null);
                    setShowCsvModal(true);
                  }}
                  disabled={isProcessingCsv}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Create HJ from CSV
                </Button>
                </>
              )}
              <Button 
                disabled={!selectedRegion}
                onClick={() => setShowAssignModal(true)}
                className={selectedRegion 
                  ? "bg-black hover:bg-gray-800 text-white" 
                  : "bg-gray-400 text-gray-600 cursor-not-allowed"
                }
              >
                <Gavel className="w-4 h-4 mr-2" />
                Manage Head Judge Groups - Events
              </Button>
            </div>
          </div>

          {/* Region Filter Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Select Region :</h3>
            <div className="grid grid-cols-4 gap-4">
              <Card
                className={`cursor-pointer transition-all duration-200 ${
                  selectedRegion === 'EU'
                    ? 'ring-2 ring-black shadow-lg bg-gray-100'
                    : 'hover:shadow-md bg-white'
                }`}
                onClick={() => setSelectedRegion('EU')}
              >
                <CardContent className="p-4">
                  <div className="text-center">
                    <h4 className="font-semibold text-gray-900">EU</h4>
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-all duration-200 ${
                  selectedRegion === 'APAC'
                    ? 'ring-2 ring-black shadow-lg bg-gray-100'
                    : 'hover:shadow-md bg-white'
                }`}
                onClick={() => setSelectedRegion('APAC')}
              >
                <CardContent className="p-4">
                  <div className="text-center">
                    <h4 className="font-semibold text-gray-900">APAC</h4>
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-all duration-200 ${
                  selectedRegion === 'AFRICA'
                    ? 'ring-2 ring-black shadow-lg bg-gray-100'
                    : 'hover:shadow-md bg-white'
                }`}
                onClick={() => setSelectedRegion('AFRICA')}
              >
                <CardContent className="p-4">
                  <div className="text-center">
                    <h4 className="font-semibold text-gray-900">AFRICA</h4>
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-all duration-200 ${
                  selectedRegion === 'AMERICAS'
                    ? 'ring-2 ring-black shadow-lg bg-gray-100'
                    : 'hover:shadow-md bg-white'
                }`}
                onClick={() => setSelectedRegion('AMERICAS')}
              >
                <CardContent className="p-4">
                  <div className="text-center">
                    <h4 className="font-semibold text-gray-900">AMERICAS</h4>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Head Judges & Race Directors Assignment</h3>
              {selectedRegion && eventsWithJudgeSets.length > 0 && (
                <Button
                  onClick={async () => {
                    if (!user?.email) {
                      alert('User email not available. Please log in again.');
                      return;
                    }

                    const eventsToSave: EventActiveStatus[] = eventsWithJudgeSets.map(event => ({
                      eventid: event.eventid,
                      islive: event.judgesetnum !== null && activeEvents[event.judgesetnum] === event.eventid,
                      judgesetnum: event.judgesetnum
                    }));

                    setIsSavingActiveStatus(true);
                    try {
                      const result = await saveJudgesetEventActiveStatus(eventsToSave, user.email);
                      if (result.success) {
                        alert(result.message || `Successfully saved active status for ${result.updated || eventsToSave.length} event(s)`);
                        
                        if (selectedRegion) {
                          setIsLoadingEventsWithJudgeSets(true);
                          try {
                            const refreshResult = await getEventsWithJudgeSetsByRegion(selectedRegion);
                            if (refreshResult.success) {
                              setEventsWithJudgeSets(refreshResult.events);
                              const updatedActiveEvents: Record<number, number | null> = {};
                              refreshResult.events.forEach(event => {
                                if (event.judgesetnum !== null) {
                                  if (event.islive) {
                                    updatedActiveEvents[event.judgesetnum] = event.eventid;
                                  } else {
                                    if (!(event.judgesetnum in updatedActiveEvents)) {
                                      updatedActiveEvents[event.judgesetnum] = null;
                                    }
                                  }
                                }
                              });
                              setActiveEvents(updatedActiveEvents);
                            } else {
                              console.error('Failed to refresh events with judge sets:', refreshResult.error);
                            }
                          } catch (refreshError) {
                            console.error('Error refreshing events with judge sets:', refreshError);
                          } finally {
                            setIsLoadingEventsWithJudgeSets(false);
                          }
                        }
                      } else {
                        alert(`Failed to save active status: ${result.error || 'Unknown error'}`);
                      }
                    } catch (error) {
                      console.error('Error saving active status:', error);
                      alert('Failed to save active status. Please try again.');
                    } finally {
                      setIsSavingActiveStatus(false);
                    }
                  }}
                  disabled={isSavingActiveStatus}
                  className={isSavingActiveStatus 
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed" 
                    : "bg-black hover:bg-gray-800 text-white"
                  }
                >
                  {isSavingActiveStatus ? (
                    <>
                      <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              )}
            </div>
            {!selectedRegion ? (
              <div className="text-center py-8 text-gray-500">
                Please select a region to view events
              </div>
            ) : isLoadingEventsWithJudgeSets ? (
              <div className="text-center py-8 text-gray-500">
                Loading events...
              </div>
            ) : eventsWithJudgeSets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No events found for {selectedRegion} region
              </div>
            ) : (
              <div className="space-y-2">
                {eventsWithJudgeSets
                  .slice()
                  .sort((a, b) => {
                    const dateA = a.eventdate ? new Date(a.eventdate).getTime() : 0;
                    const dateB = b.eventdate ? new Date(b.eventdate).getTime() : 0;
                    
                    if (dateA !== dateB) {
                      return dateA - dateB;
                    }
                    
                    const nameA = a.eventname || '';
                    const nameB = b.eventname || '';
                    return nameA.localeCompare(nameB);
                  })
                  .map((event) => (
                  <div 
                    key={event.eventid} 
                    className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="grid grid-cols-6 gap-4 items-center">
                      <div className="col-span-2">
                        <div className="font-medium text-gray-900">{event.eventname}</div>
                        {event.eventdate && (
                          <div className="text-sm text-gray-600 mt-1">
                            {formatDateForDisplay(event.eventdate)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Judge Set #</div>
                        <div className="text-sm font-medium text-gray-900">
                          {event.judgesetnum !== null ? event.judgesetnum : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Set LIVE</div>
                        <button
                          onClick={() => {
                            if (event.judgesetnum === null) return;
                            
                            const currentActiveEventId = activeEvents[event.judgesetnum];
                            const isCurrentlyActive = currentActiveEventId === event.eventid;
                            
                            if (isCurrentlyActive) {
                              setActiveEvents(prev => ({
                                ...prev,
                                [event.judgesetnum!]: null
                              }));
                            } else {
                              setActiveEvents(prev => ({
                                ...prev,
                                [event.judgesetnum!]: event.eventid
                              }));
                            }
                          }}
                          disabled={event.judgesetnum === null}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            event.judgesetnum === null
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : activeEvents[event.judgesetnum] === event.eventid
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {event.judgesetnum !== null && activeEvents[event.judgesetnum] === event.eventid
                            ? 'ACTIVE'
                            : 'INACTIVE'}
                        </button>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Head Judge App Login : QR Codes</div>
                        <div 
                          className={`text-sm font-medium ${
                            event.judgesetnum !== null 
                              ? 'text-blue-600 hover:text-blue-800 cursor-pointer underline' 
                              : 'text-gray-900'
                          }`}
                          onClick={() => {
                            if (event.judgesetnum !== null) {
                              setSelectedJudgesetnum(event.judgesetnum);
                              setShowCredsModal(true);
                            }
                          }}
                        >
                          {event.judgesetdesc || '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Race Directors</div>
                        <div className="flex flex-wrap gap-1">
                          {event.timingone && (
                            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md">
                              {event.timingone}
                            </span>
                          )}
                          {event.timingtwo && (
                            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md">
                              {event.timingtwo}
                            </span>
                          )}
                          {!event.timingone && !event.timingtwo && (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create HJ from CSV Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Create HJ from CSV</h2>
              <button
                type="button"
                onClick={() => setShowCsvModal(false)}
                disabled={isProcessingCsv}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="text-sm text-gray-600 mb-4 space-y-1">
              <p>
                Upload a CSV with columns <code className="font-mono text-xs bg-gray-100 px-1">email</code>,{' '}
                <code className="font-mono text-xs bg-gray-100 px-1">displayname</code>,{' '}
                <code className="font-mono text-xs bg-gray-100 px-1">roleid</code>.
              </p>
              <p>
                Password for each user: first 4 characters of email + <code className="font-mono text-xs bg-gray-100 px-1">@hj</code>{' '}
                (e.g. <code className="font-mono text-xs bg-gray-100 px-1">suni@hj</code>).
              </p>
            </div>

            <div className="mb-4">
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={isProcessingCsv}
                onChange={(e) => {
                  setCsvFile(e.target.files?.[0] ?? null);
                  setCsvParseError('');
                  setCsvResults(null);
                  setCsvSummary(null);
                }}
                className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-900 file:text-white hover:file:bg-gray-800"
              />
              {csvFile && (
                <p className="text-xs text-gray-500 mt-2">Selected: {csvFile.name}</p>
              )}
            </div>

            {csvParseError && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {csvParseError}
              </div>
            )}

            <div className="flex gap-3 mb-6">
              <Button
                type="button"
                onClick={handleProcessCsv}
                disabled={isProcessingCsv || !csvFile}
                className={
                  isProcessingCsv || !csvFile
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-emerald-700 hover:bg-emerald-800 text-white'
                }
              >
                {isProcessingCsv ? (
                  <>
                    <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Process
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCsvModal(false)}
                disabled={isProcessingCsv}
              >
                Close
              </Button>
            </div>

            {csvSummary && (
              <div className="mb-3 text-sm text-gray-700">
                Created: <span className="font-semibold text-green-700">{csvSummary.created}</span>
                {' · '}
                Skipped: <span className="font-semibold text-amber-700">{csvSummary.skipped}</span>
                {' · '}
                Failed: <span className="font-semibold text-red-700">{csvSummary.failed}</span>
              </div>
            )}

            {csvResults && csvResults.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[40vh]">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Email</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Auth status</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Display name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvResults.map((row, idx) => (
                        <tr key={`${row.email}-${idx}`} className="border-b last:border-b-0">
                          <td className="px-3 py-2 text-gray-900 font-mono text-xs">{row.email}</td>
                          <td className="px-3 py-2">
                            {row.authStatus === 'success' ? (
                              <span className="text-green-700 font-semibold">success</span>
                            ) : (
                              <span className="text-red-700">
                                fail{row.authError ? ` — ${row.authError}` : ''}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-900">{row.displayname || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Head Judge Modal */}
      {showAssignModal && selectedRegion && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{selectedRegion} : Assign Head Judges + Race Directors</h2>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {isLoadingJudgeSets || isLoadingRegionEvents || isLoadingRaceDirectors ? (
              <div className="text-center py-8 text-gray-500">
                Loading...
              </div>
            ) : judgeSets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No judge sets available for {selectedRegion} region
              </div>
            ) : regionEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No events found for {selectedRegion} region
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  Select a judge set and race directors for each event:
                </div>
                <div className="space-y-3">
                  {regionEvents.map((event) => {
                    const selectedEmails = eventRaceDirectorsAssignments[event.eventid] || [];
                    const isTimingDropdownOpen = openTimingDropdown === event.eventid;
                    
                    return (
                      <div 
                        key={event.eventid}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="mb-3">
                          <div className="font-medium text-gray-900 mb-1">{event.eventname}</div>
                          {event.eventdate && (
                            <div className="text-sm text-gray-600">
                              {formatDateForDisplay(event.eventdate)}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                              Select set of Head Judges
                            </label>
                            <Select
                              value={eventJudgeSetAssignments[event.eventid]?.toString() || undefined}
                              onValueChange={(value) => {
                                setEventJudgeSetAssignments(prev => ({
                                  ...prev,
                                  [event.eventid]: value ? parseInt(value) : null
                                }));
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select judge set" />
                              </SelectTrigger>
                              <SelectContent>
                                {judgeSets.map((judgeSet) => (
                                  <SelectItem 
                                    key={judgeSet.judgesetnum} 
                                    value={judgeSet.judgesetnum.toString()}
                                  >
                                    {judgeSet.judgesetdesc} ({judgeSet.judgesetnum})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="relative">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                              Select upto 4 Race Directors
                            </label>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setOpenTimingDropdown(isTimingDropdownOpen ? null : event.eventid)}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm border border-input rounded-md bg-transparent shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <span className="text-muted-foreground">
                                  {selectedEmails.length > 0 
                                    ? `${selectedEmails.length} selected` 
                                    : 'Select race directors'}
                                </span>
                                <ChevronDown className="h-4 w-4 opacity-50" />
                              </button>
                              {isTimingDropdownOpen && (
                                <>
                                  <div 
                                    className="fixed inset-0 z-40" 
                                    onClick={() => setOpenTimingDropdown(null)}
                                  />
                                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {availableRaceDirectors.length === 0 ? (
                                      <div className="px-3 py-2 text-sm text-gray-500">
                                        No race directors available
                                      </div>
                                    ) : (
                                      availableRaceDirectors.map((email) => {
                                        const isSelected = selectedEmails.includes(email);
                                        return (
                                          <div
                                            key={email}
                                            className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                            onClick={() => {
                                              setEventRaceDirectorsAssignments(prev => {
                                                const current = prev[event.eventid] || [];
                                                if (isSelected) {
                                                  return {
                                                    ...prev,
                                                    [event.eventid]: current.filter(e => e !== email)
                                                  };
                                                } else {
                                                  if (current.length >= 2) {
                                                    alert('You can select up to 2 race directors per event.');
                                                    return prev;
                                                  }
                                                  return {
                                                    ...prev,
                                                    [event.eventid]: [...current, email]
                                                  };
                                                }
                                              });
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              disabled={!isSelected && selectedEmails.length >= 2}
                                              onChange={() => {}}
                                              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                            <span className={`text-sm ${!isSelected && selectedEmails.length >= 2 ? 'text-gray-400' : 'text-gray-700'}`}>{email}</span>
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            {selectedEmails.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {selectedEmails.map((email) => (
                                  <span
                                    key={email}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                                  >
                                    {email}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEventRaceDirectorsAssignments(prev => ({
                                          ...prev,
                                          [event.eventid]: (prev[event.eventid] || []).filter(e => e !== email)
                                        }));
                                      }}
                                      className="hover:bg-blue-200 rounded-full p-0.5"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowAssignModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      const mappings: SaveHjSetMapping[] = Object.entries(eventJudgeSetAssignments)
                        .filter(([_, judgesetnum]) => judgesetnum !== null)
                        .map(([eventid, judgesetnum]) => {
                          const eventIdNum = parseInt(eventid);
                          const selectedRaceDirectors = eventRaceDirectorsAssignments[eventIdNum] || [];
                          const timingone = selectedRaceDirectors[0] || null;
                          const timingtwo = selectedRaceDirectors[1] || null;
                          
                          return {
                            eventid: eventIdNum,
                            judgesetnum: judgesetnum as number,
                            timingone: timingone,
                            timingtwo: timingtwo
                          };
                        });

                      if (mappings.length === 0) {
                        alert('Please select at least one judge set assignment before saving.');
                        return;
                      }

                      if (!user?.email) {
                        alert('User email not available. Please log in again.');
                        return;
                      }

                      try {
                        const result = await saveHjSetForEvent(mappings, user.email);
                        if (result.success) {
                          alert(result.message || `Successfully saved ${mappings.length} assignment(s)`);
                          setShowAssignModal(false);
                          
                          if (selectedRegion) {
                            setIsLoadingEventsWithJudgeSets(true);
                            try {
                              const refreshResult = await getEventsWithJudgeSetsByRegion(selectedRegion);
                              if (refreshResult.success) {
                                setEventsWithJudgeSets(refreshResult.events);
                              } else {
                                console.error('Failed to refresh events with judge sets:', refreshResult.error);
                              }
                            } catch (refreshError) {
                              console.error('Error refreshing events with judge sets:', refreshError);
                            } finally {
                              setIsLoadingEventsWithJudgeSets(false);
                            }
                          }
                        } else {
                          alert(`Failed to save assignments: ${result.error || 'Unknown error'}`);
                        }
                      } catch (error) {
                        console.error('Error saving assignments:', error);
                        alert('Failed to save assignments. Please try again.');
                      }
                    }}
                    className="bg-black hover:bg-gray-800 text-white"
                  >
                    Save Assignments
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Head Judge User Credentials</h2>
              <div className="flex gap-2">
                {credentials.length > 0 && (
                  <Button
                    onClick={handlePrintToPDF}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Download Passwords
                  </Button>
                )}
                <button
                  onClick={() => {
                    setShowCredsModal(false);
                    setSelectedJudgesetnum(null);
                    setCredentials([]);
                  }}
                  className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {isLoadingCreds ? (
              <div className="text-center py-8 text-gray-500">
                Loading credentials...
              </div>
            ) : credentials.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No credentials found for this judge set
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  Judge Set #{selectedJudgesetnum} - {credentials.length} user(s)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-3 text-left font-medium text-gray-700 border border-gray-300">Sign-in / e-mail</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700 border border-gray-300">QR Code : Head Judge App Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credentials.map((cred, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 border border-gray-300 text-gray-900">{cred.email}</td>
                          <td className="px-4 py-3 border border-gray-300">
                            <Button
                              onClick={() => handleGenerateQR(cred.email, cred.password)}
                              className="bg-gray-600 hover:bg-gray-800 text-white text-xs px-3 py-1"
                              size="sm"
                            >
                              Show QR Code
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCredsModal(false);
                      setSelectedJudgesetnum(null);
                      setCredentials([]);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedQRData && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Penalty App Login : QR Code</h2>
              <button
                onClick={() => {
                  setShowQRModal(false);
                  setSelectedQRData(null);
                }}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <QRCodeSVG
                  value={JSON.stringify({
                    email: selectedQRData.email,
                    password: selectedQRData.password
                  })}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="text-sm text-gray-600 text-center">
                <p className="font-medium mb-1">Head Judge: {selectedQRData.email}</p>
                <p className="text-xs">Scan this QR code using Head Judge App to login</p>
              </div>
              <Button
                onClick={() => {
                  setShowQRModal(false);
                  setSelectedQRData(null);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

